import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import Footer from "../components/app/Footer";
import SEOHead from "../components/app/SEOHead";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import { toast } from "sonner";
import { CaretDown, PaperPlaneTilt, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";

// Generic markdown-driven page for /about, /privacy, /kvkk
export function ContentPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { lang } = useT();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/content/${slug}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const title = doc?.title?.[lang] || doc?.title?.en || "";
  const body = doc?.body?.[lang] || doc?.body?.en || "";

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid={`content-page-${slug}`}>
        {loading ? (
          <div className="h-64 grid place-items-center">
            <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
          </div>
        ) : (
          <>
            <div className="overline text-[#002FA7]">{slug.toUpperCase()}</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2" data-testid={`content-title-${slug}`}>{title}</h1>
            <article className="prose prose-slate max-w-none mt-8 prose-headings:font-display prose-headings:tracking-tight prose-h2:text-2xl prose-h3:text-lg prose-a:text-[#002FA7]" data-testid={`content-body-${slug}`}>
              <ReactMarkdown>{body}</ReactMarkdown>
            </article>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export function FAQPage() {
  const { lang, t } = useT();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/content/faq").then(({ data }) => setItems(data?.data?.items || [])).catch(() => {});
  }, []);

  // FAQPage structured data → Google FAQ rich results
  const faqJsonLd = items.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: lang === "tr" ? it.question_tr : it.question_en,
      acceptedAnswer: {
        "@type": "Answer",
        text: lang === "tr" ? it.answer_tr : it.answer_en,
      },
    })),
  } : null;

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead pageKey="faq" jsonLd={faqJsonLd} />
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid="faq-page">
        <div className="overline text-[#002FA7]">FAQ</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">{t("nav.faq")}</h1>
        <p className="text-slate-600 mt-3">{t("faq.subtitle")}</p>
        <div className="mt-10 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="card !p-0 overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-semibold">{lang === "tr" ? it.question_tr : it.question_en}</span>
                <CaretDown size={18} weight="bold" className={`text-slate-400 transition-transform shrink-0 ${open === i ? "rotate-180 text-[#002FA7]" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-slate-700 leading-relaxed text-sm border-t border-slate-100" data-testid={`faq-answer-${i}`}>
                  <div className="pt-3">{lang === "tr" ? it.answer_tr : it.answer_en}</div>
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="text-slate-500 text-sm">…</div>}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function ContactPage() {
  const { lang, t } = useT();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/content/contact_info").then(({ data }) => setInfo(data?.data || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid="contact-page">
        <div className="overline text-[#002FA7]">{t("nav.contact").toUpperCase()}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">{t("contact.title")}</h1>
        <p className="text-slate-600 mt-3 max-w-xl">{t("contact.subtitle")}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-5 md:col-span-1">
            {info?.email && (
              <div className="flex items-start gap-3">
                <EnvelopeSimple size={22} className="text-[#002FA7] mt-1" weight="duotone" />
                <div>
                  <div className="overline">Email</div>
                  <a href={`mailto:${info.email}`} className="text-sm hover:text-[#002FA7]">{info.email}</a>
                </div>
              </div>
            )}
            {info?.phone && (
              <div className="flex items-start gap-3">
                <Phone size={22} className="text-[#002FA7] mt-1" weight="duotone" />
                <div>
                  <div className="overline">{t("contact.phone")}</div>
                  <span className="text-sm">{info.phone}</span>
                </div>
              </div>
            )}
            {(info?.address_tr || info?.address_en) && (
              <div className="flex items-start gap-3">
                <MapPin size={22} className="text-[#002FA7] mt-1" weight="duotone" />
                <div>
                  <div className="overline">{t("contact.address")}</div>
                  <span className="text-sm">{lang === "tr" ? info.address_tr : info.address_en}</span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="card md:col-span-2 space-y-3" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="overline">{t("common.name")}</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div>
                <label className="overline">{t("common.email")}</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <label className="overline">{t("contact.subject")}</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
            </div>
            <div>
              <label className="overline">{t("contact.message")}</label>
              <textarea required minLength={5} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="contact-submit-button">
              <PaperPlaneTilt size={16} weight="bold" /> {submitting ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function PublicHeader() {
  const { t } = useT();
  return (
    <header className="glass sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="h-7 w-7 rounded-sm bg-[#002FA7] grid place-items-center text-white font-display font-bold">P</div>
          <span className="font-display text-lg font-bold tracking-tight">PMCoach AI</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-[#002FA7] hidden sm:inline">{t("common.login")}</Link>
          <Link to="/register" className="btn-primary !py-2 !px-4 text-sm">{t("landing.cta.primary")}</Link>
        </div>
      </div>
    </header>
  );
}
